import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FaChevronDown, FaCheck, FaPen } from 'react-icons/fa';
import CheckboxGroup from './CheckboxGroup';
import { conditions_summary } from './gen_config_utils';

// Accent colour per generation mode (left border + dot).
const MODE_ACCENT = {
  yes: '#16a34a',
  no: '#9ca3af',
  previous_red: '#dc2626',
  previous_yellow: '#d97706'
};

const EMPTY_CONDITIONS = { prepay_type: [], plan_type: [], account_status: [] };

// One checkpoint row of the "Checklist generation" section. Collapsed it shows
// the checkpoint label + current mode (and conditions summary) like the custom
// rule cards; "Edit" expands the mode dropdown (Yes / No / previous check-in
// colour for Day 15) and — on Day 22 / Week 7 / Week 11 — the sub-condition
// panel under "No": checkbox groups for prepay type, plan type and account
// status, ANDed across groups, ORed within.
class GenCheckpointCard extends Component {
  state = { open: false, expanded: false };

  accent = () => {
    const { checkpoint } = this.props;
    if (
      checkpoint.mode === 'no' &&
      checkpoint.supports_conditions &&
      conditions_summary(checkpoint.conditions, this.props.meta)
    ) {
      return '#d97706'; // no + conditions = conditional generation
    }
    return MODE_ACCENT[checkpoint.mode] || '#6b7280';
  };

  dot = (mode) => (
    <span
      style={{
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: 9999,
        background: MODE_ACCENT[mode] || '#6b7280',
        flexShrink: 0
      }}
    />
  );

  toggle_open = () => {
    if (!this.props.saving) this.setState((prev) => ({ open: !prev.open }));
  };

  toggle_expanded = () => {
    if (!this.props.saving) {
      this.setState((prev) => ({ expanded: !prev.expanded, open: false }));
    }
  };

  // One-line status shown while collapsed: the mode label, plus the conditions
  // summary when "No" generates conditionally.
  status_summary = () => {
    const { checkpoint, meta } = this.props;
    const current = checkpoint.options.find((o) => o.value === checkpoint.mode);
    const label = current ? current.label : checkpoint.mode;
    if (checkpoint.mode === 'no' && checkpoint.supports_conditions) {
      const summary = conditions_summary(checkpoint.conditions, meta);
      if (summary) return `${label} — only for: ${summary}`;
    }
    return label;
  };

  pick_mode = (mode) => {
    this.setState({ open: false });
    this.props.onModeChange(this.props.checkpoint.check_type, mode);
  };

  set_condition_group = (group, values) => {
    const { checkpoint, onConditionsChange } = this.props;
    onConditionsChange(checkpoint.check_type, {
      ...EMPTY_CONDITIONS,
      ...(checkpoint.conditions || {}),
      [group]: values
    });
  };

  // Custom dropdown (native <select> can't style its option popup). A styled
  // trigger opens a menu; a transparent full-screen layer closes it on outside
  // click. Divs (not buttons) dodge the host CMS's global button rule.
  render_dropdown = () => {
    const { checkpoint, saving } = this.props;
    const { open } = this.state;
    const current = checkpoint.options.find((o) => o.value === checkpoint.mode);
    return (
      <div className="relative">
        <div
          role="button"
          tabIndex={0}
          aria-disabled={saving}
          onClick={this.toggle_open}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !saving) {
              e.preventDefault();
              this.toggle_open();
            }
          }}
          className={`w-full flex items-center justify-between gap-2 rounded-md border bg-white px-3 py-1.5 text-sm text-gray-800 select-none ${
            saving
              ? 'border-gray-200 opacity-60 cursor-wait'
              : 'border-gray-300 hover:border-gray-400 cursor-pointer'
          } ${open ? 'ring-2 ring-blue-400 border-blue-400' : ''}`}
        >
          <span className="flex items-center gap-2">
            {this.dot(checkpoint.mode)}
            {current ? current.label : checkpoint.mode}
          </span>
          <FaChevronDown size={11} color="#9ca3af" />
        </div>
        {open ? (
          <>
            <div className="fixed inset-0 z-10" onClick={() => this.setState({ open: false })} />
            <div className="absolute left-0 right-0 z-20 mt-1 rounded-md border border-gray-200 bg-white shadow-lg overflow-hidden">
              {checkpoint.options.map((o) => {
                const selected = o.value === checkpoint.mode;
                return (
                  <div
                    key={o.value}
                    role="button"
                    tabIndex={0}
                    onClick={() => this.pick_mode(o.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        this.pick_mode(o.value);
                      }
                    }}
                    className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 ${
                      selected ? 'bg-blue-50 font-medium' : ''
                    }`}
                    style={{ color: '#1f2937' }}
                  >
                    {this.dot(o.value)}
                    <span className="flex-1">{o.label}</span>
                    {selected ? <FaCheck size={11} color="#2563eb" /> : null}
                  </div>
                );
              })}
            </div>
          </>
        ) : null}
      </div>
    );
  };

  render_conditions = () => {
    const { checkpoint, meta, saving } = this.props;
    const conditions = { ...EMPTY_CONDITIONS, ...(checkpoint.conditions || {}) };
    const summary = conditions_summary(conditions, meta);
    return (
      <div
        className="mt-2 rounded-md border border-gray-200 bg-white px-3 py-2 flex flex-col gap-2"
        data-testid={`gen-conditions-${checkpoint.check_type}`}
      >
        <CheckboxGroup
          label="Prepay type"
          options={meta.prepay_options || []}
          selected={conditions.prepay_type}
          onChange={(v) => this.set_condition_group('prepay_type', v)}
          disabled={saving}
        />
        <CheckboxGroup
          label="Plan type"
          options={meta.plan_options || []}
          selected={conditions.plan_type}
          onChange={(v) => this.set_condition_group('plan_type', v)}
          disabled={saving}
        />
        <CheckboxGroup
          label="Account status"
          options={meta.status_options || []}
          selected={conditions.account_status}
          onChange={(v) => this.set_condition_group('account_status', v)}
          disabled={saving}
        />
        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {summary
            ? `Generates only for members matching: ${summary}`
            : 'No conditions selected — never generates.'}
        </div>
      </div>
    );
  };

  render() {
    const { checkpoint, saving } = this.props;
    const { expanded } = this.state;
    const show_conditions = checkpoint.supports_conditions && checkpoint.mode === 'no';
    return (
      <div
        className="rounded-lg border border-gray-200 bg-gray-50/40 p-3"
        style={{ borderLeft: `3px solid ${this.accent()}` }}
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-gray-800" style={{ flexShrink: 0 }}>
            {checkpoint.label}
          </span>
          <span
            className="flex-1 text-sm inline-flex items-center gap-2"
            style={{ color: 'var(--text-muted)' }}
          >
            {this.dot(checkpoint.mode)}
            {this.status_summary()}
          </span>
          <div
            role="button"
            tabIndex={0}
            aria-label={`Edit ${checkpoint.label} generation`}
            onClick={this.toggle_expanded}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && !saving) {
                e.preventDefault();
                this.toggle_expanded();
              }
            }}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium cursor-pointer hover:bg-blue-50 ${
              expanded ? 'bg-blue-50 text-blue-700' : 'text-gray-600'
            }`}
          >
            <FaPen size={10} /> {expanded ? 'Close' : 'Edit'}
          </div>
        </div>
        {expanded ? (
          <div className="mt-2">
            {/* Keep the mode dropdown compact; the conditions panel below it
                takes the row's full width so checkbox groups don't wrap. */}
            <div style={{ maxWidth: 360 }}>{this.render_dropdown()}</div>
            {show_conditions ? this.render_conditions() : null}
          </div>
        ) : null}
      </div>
    );
  }
}

GenCheckpointCard.propTypes = {
  checkpoint: PropTypes.shape({
    check_type: PropTypes.string,
    label: PropTypes.string,
    mode: PropTypes.string,
    conditions: PropTypes.shape({
      prepay_type: PropTypes.arrayOf(PropTypes.string),
      plan_type: PropTypes.arrayOf(PropTypes.string),
      account_status: PropTypes.arrayOf(PropTypes.string)
    }),
    options: PropTypes.arrayOf(
      PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })
    ),
    supports_conditions: PropTypes.bool
  }).isRequired,
  meta: PropTypes.shape({
    prepay_options: PropTypes.arrayOf(PropTypes.shape({})),
    plan_options: PropTypes.arrayOf(PropTypes.shape({})),
    status_options: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  saving: PropTypes.bool.isRequired,
  onModeChange: PropTypes.func.isRequired,
  onConditionsChange: PropTypes.func.isRequired
};

export default GenCheckpointCard;
